import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout, Field, inputCls, primaryBtn, ErrorText } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { errMsg } from "@/lib/api";
import { PhoneInput } from "@/components/PhoneInput";
import { GoogleButton, InvitationStatus, useInvitationCheck } from "@/components/GoogleButton";

export const LEVELS = ["N5", "N4", "N3", "N2"];

export function LevelPicker({ value, onChange, testid = "level" }) {
  return (
    <div className="grid grid-cols-4 gap-2" data-testid={`${testid}-picker`}>
      {LEVELS.map((l) => (
        <button type="button" key={l} data-testid={`${testid}-${l}`} onClick={() => onChange(l)} className={`rounded-xl border py-2.5 text-sm font-semibold transition-colors ${value === l ? "border-primary bg-primary/5 text-primary ring-1 ring-primary" : "border-border hover:bg-accent"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function Register() {
  const { register } = useAuth();
  const { t } = usePrefs();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const linkCode = (params.get("code") || "").toUpperCase();
  const [form, setForm] = useState({ name: "", email: "", password: "", invitation_code: linkCode, target_level: "N4", phone_country: "+81", phone_number: "" });
  const invSt = useInvitationCheck(form.invitation_code);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { await register(form); nav("/dashboard"); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };

  return (
    <AuthLayout title={t("register")} subtitle={t("regHint")} footer={<>{t("haveAccount")} <Link data-testid="register-login-link" to="/login" className="inline-block py-2 font-medium text-foreground underline-offset-4 hover:underline">{t("login")}</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label={t("invCode")} id="code"><input id="code" data-testid="register-code-input" required className={`${inputCls} uppercase tracking-widest`} value={form.invitation_code} onChange={set("invitation_code")} placeholder={t("invCodePh")} readOnly={!!linkCode} /><InvitationStatus st={invSt} testid="register-code-status" /></Field>
        <Field label={t("name")} id="name"><input id="name" data-testid="register-name-input" required className={inputCls} value={form.name} onChange={set("name")} /></Field>
        <Field label={t("email")} id="email"><input id="email" data-testid="register-email-input" type="email" required className={inputCls} value={form.email} onChange={set("email")} /></Field>
        <Field label={t("password")} id="password"><input id="password" data-testid="register-password-input" type="password" required minLength={6} className={inputCls} value={form.password} onChange={set("password")} /></Field>
        <PhoneInput required country={form.phone_country} number={form.phone_number} onChange={({ country, number }) => setForm({ ...form, phone_country: country, phone_number: number })} testid="register-phone" />
        <div><span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("target")}</span><LevelPicker value={form.target_level} onChange={(l) => setForm({ ...form, target_level: l })} testid="register-level" /></div>
        <ErrorText msg={err} id="register-error" />
        <button data-testid="register-submit-button" type="submit" disabled={busy} className={primaryBtn}>{t("register")}</button>
      </form>
      <GoogleButton testid="google-register-button" label={t("googleRegister")} code={form.invitation_code} level={form.target_level} requireCode />
    </AuthLayout>
  );
}
